import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { DownloadSimple, MagnifyingGlass } from "@phosphor-icons/react";

export default function Reports() {
  const [rows, setRows] = useState([]);
  const [hospitals, setHospitals] = useState([]);
  const [areas, setAreas] = useState([]);
  const [filters, setFilters] = useState({ hospital_id: "", area_id: "", status: "", start: "", end: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/masters/hospitals").then((r) => setHospitals(r.data));
    api.get("/masters/areas").then((r) => setAreas(r.data));
    run();
  }, []);

  const run = async () => {
    setLoading(true);
    const params = Object.fromEntries(Object.entries(filters).filter(([_, v]) => v));
    const { data } = await api.get("/reports/tasks", { params });
    setRows(data);
    setLoading(false);
  };

  const exportCsv = async () => {
    const params = Object.fromEntries(Object.entries(filters).filter(([_, v]) => v));
    const token = localStorage.getItem("hk_token");
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/reports/tasks/export?` + new URLSearchParams(params);
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.download = "tasks_report.csv";
    link.click();
  };

  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  return (
    <div className="space-y-6" data-testid="reports-page">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-small text-blue-700">Operational Insights</div>
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900 mt-1" style={{ fontFamily: "Manrope" }}>Reports</h1>
          <p className="text-sm text-slate-600 mt-1">Filter, analyse, export.</p>
        </div>
        <button data-testid="export-csv-button" onClick={exportCsv} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-md inline-flex items-center gap-2 transition-colors">
          <DownloadSimple size={14} weight="bold" /> Export CSV
        </button>
      </header>

      <div className="flat-card p-4 grid grid-cols-2 md:grid-cols-5 gap-3">
        <div>
          <label className="label-small block mb-1">Hospital</label>
          <select data-testid="filter-hospital" value={filters.hospital_id} onChange={(e) => set("hospital_id", e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm bg-white focus:outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600">
            <option value="">All</option>
            {hospitals.map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}
          </select>
        </div>
        <div>
          <label className="label-small block mb-1">Area</label>
          <select data-testid="filter-area" value={filters.area_id} onChange={(e) => set("area_id", e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm bg-white focus:outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600">
            <option value="">All</option>
            {areas.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </div>
        <div>
          <label className="label-small block mb-1">Status</label>
          <select data-testid="filter-status" value={filters.status} onChange={(e) => set("status", e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm bg-white focus:outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600">
            <option value="">All</option>
            {["pending", "in_progress", "completed", "verified", "rejected", "escalated", "overdue"].map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <label className="label-small block mb-1">From</label>
          <input data-testid="filter-start" type="date" value={filters.start} onChange={(e) => set("start", e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm focus:outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600" />
        </div>
        <div>
          <label className="label-small block mb-1">To</label>
          <input data-testid="filter-end" type="date" value={filters.end} onChange={(e) => set("end", e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm focus:outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600" />
        </div>
        <div className="col-span-2 md:col-span-5">
          <button data-testid="apply-filters" onClick={run} className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold rounded-md inline-flex items-center gap-2 transition-colors">
            <MagnifyingGlass size={14} /> Apply filters
          </button>
        </div>
      </div>

      <div className="flat-card">
        <table className="data-table" data-testid="report-table">
          <thead>
            <tr>
              <th>Area</th><th>Checklist</th><th>Status</th><th>Staff</th><th>Verified By</th><th>Verification Remarks</th><th>Scheduled</th><th>Submitted</th><th>Verified</th><th>SLA</th>
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={10} className="text-center py-6 text-slate-500">Loading…</td></tr> :
              rows.length === 0 ? <tr><td colSpan={10} className="text-center py-6 text-slate-500">No data.</td></tr> :
                rows.map((r) => (
                  <tr key={r.id}>
                    <td className="font-semibold text-slate-900">{r.area_name}</td>
                    <td className="text-xs">{r.checklist_name || "—"}</td>
                    <td><span className={`status-pill status-${r.status}`}>{r.status.replace("_", " ")}</span></td>
                    <td>{r.staff_name}</td>
                    <td className="text-slate-700">{r.verified_by_name || "—"}</td>
                    <td className="text-xs text-slate-600 max-w-[200px] truncate" title={r.verification_remarks || ""}>{r.verification_remarks || "—"}</td>
                    <td className="text-xs">{r.scheduled_at ? new Date(r.scheduled_at).toLocaleString() : "—"}</td>
                    <td className="text-xs">{r.submitted_at ? new Date(r.submitted_at).toLocaleString() : "—"}</td>
                    <td className="text-xs">{r.verified_at ? new Date(r.verified_at).toLocaleString() : "—"}</td>
                    <td>{r.sla_breached ? <span className="label-small text-red-700">Breached</span> : <span className="label-small text-green-700">On time</span>}</td>
                  </tr>
                ))
            }
          </tbody>
        </table>
      </div>
    </div>
  );
}
